// Statistical utilities for AB testing calculations
import { 
  erf, 
  gamma
} from 'mathjs';

// Normal distribution functions
export const normalCDF = (x, mean = 0, stdDev = 1) => {
  return 0.5 * (1 + erf((x - mean) / (stdDev * Math.sqrt(2))));
};

export const normalPDF = (x, mean = 0, stdDev = 1) => {
  const coefficient = 1 / (stdDev * Math.sqrt(2 * Math.PI));
  const exponent = -0.5 * Math.pow((x - mean) / stdDev, 2);
  return coefficient * Math.exp(exponent);
};

// Beta probability density function
export const betaPDF = (x, alpha, beta) => {
  if (x <= 0 || x >= 1) return 0;
  
  const logBeta = Math.log(gamma(alpha)) + Math.log(gamma(beta)) - Math.log(gamma(alpha + beta));
  const logPdf = (alpha - 1) * Math.log(x) + (beta - 1) * Math.log(1 - x) - logBeta;
  
  return Math.exp(logPdf);
};

// Generate probability density data for plotting
export const generateProbabilityDensityData = (
  controlConversions, 
  controlSessions, 
  variantConversions, 
  variantSessions,
  priorAlpha = 1,
  priorBeta = 1
) => {
  // Posterior parameters
  const posteriorAlphaControl = priorAlpha + controlConversions;
  const posteriorBetaControl = priorBeta + controlSessions - controlConversions;
  
  const posteriorAlphaVariant = priorAlpha + variantConversions;
  const posteriorBetaVariant = priorBeta + variantSessions - variantConversions;
  
  // Generate x values (conversion rates from 0 to 1)
  const points = 200;
  const xValues = [];
  const controlDensity = [];
  const variantDensity = [];
  
  for (let i = 0; i <= points; i++) {
    const x = i / points;
    xValues.push(x);
    controlDensity.push(betaPDF(x, posteriorAlphaControl, posteriorBetaControl));
    variantDensity.push(betaPDF(x, posteriorAlphaVariant, posteriorBetaVariant));
  }
  
  return {
    xValues,
    controlDensity,
    variantDensity,
    controlMean: posteriorAlphaControl / (posteriorAlphaControl + posteriorBetaControl),
    variantMean: posteriorAlphaVariant / (posteriorAlphaVariant + posteriorBetaVariant)
  };
};

// Calculate additional days needed for significance
export const calculateAdditionalDaysNeeded = (
  controlConversions,
  controlSessions,
  variantConversions,
  variantSessions,
  currentTestDays,
  dailyTraffic,
  trafficSplit = 0.5,
  targetPower = 0.8,
  alpha = 0.05
) => {
  const controlRate = controlConversions / controlSessions;
  const variantRate = variantConversions / variantSessions;
  const observedEffect = Math.abs(variantRate - controlRate) / controlRate;
  
  if (observedEffect === 0) {
    return {
      additionalDays: Infinity,
      message: "No effect observed - test may need to run indefinitely"
    };
  }
  
  // Calculate required sample size for observed effect
  const requiredSampleSize = calculateSampleSize(
    controlRate,
    observedEffect,
    alpha,
    targetPower,
    2
  );
  
  const currentSampleSize = controlSessions + variantSessions;
  const dailyTestTraffic = dailyTraffic * trafficSplit;
  
  if (currentSampleSize >= requiredSampleSize) {
    return {
      additionalDays: 0,
      message: "Sufficient sample size reached"
    };
  }
  
  const additionalSampleNeeded = requiredSampleSize - currentSampleSize;
  const additionalDays = Math.ceil(additionalSampleNeeded / dailyTestTraffic);
  
  return {
    additionalDays,
    requiredSampleSize,
    currentSampleSize,
    message: `${additionalDays} additional days needed for ${targetPower * 100}% power`
  };
};

// Calculate extra transactions (monthly/yearly impact)
export const calculateExtraTransactions = (
  controlConversions,
  controlSessions,
  variantConversions,
  variantSessions,
  dailyTraffic,
  trafficSplit = 0.5,
  averageOrderValue = null,
  testDuration = null
) => {
  const controlRate = controlConversions / controlSessions;
  const variantRate = variantConversions / variantSessions;
  const absoluteLift = variantRate - controlRate; // Absolute lift
  const relativeLift = controlRate > 0 ? (variantRate - controlRate) / controlRate : 0; // Relative lift (percentage)
  
  if (absoluteLift <= 0) {
    return {
      dailyExtraTransactions: 0,
      monthlyExtraTransactions: 0,
      yearlyExtraTransactions: 0,
      dailyExtraRevenue: 0,
      monthlyExtraRevenue: 0,
      yearlyExtraRevenue: 0,
      message: "No positive lift observed"
    };
  }
  
  // Calculate daily total traffic (control + variant combined)
  let dailyTotalTraffic;
  if (testDuration && testDuration > 0) {
    // Use actual total traffic per day from test
    dailyTotalTraffic = (controlSessions + variantSessions) / testDuration;
  } else {
    // Use provided daily traffic (assuming it represents total traffic)
    dailyTotalTraffic = dailyTraffic;
  }
  
  // Calculate daily extra transactions using absolute lift
  // If winning variant was deployed to all traffic:
  // Daily extra = (daily total traffic * absolute lift)
  const dailyExtraTransactions = dailyTotalTraffic * absoluteLift;
  const monthlyExtraTransactions = dailyExtraTransactions * 30;
  const yearlyExtraTransactions = dailyExtraTransactions * 365;
  
  let dailyExtraRevenue = 0;
  let monthlyExtraRevenue = 0;
  let yearlyExtraRevenue = 0;
  
  if (averageOrderValue && averageOrderValue > 0) {
    dailyExtraRevenue = dailyExtraTransactions * averageOrderValue;
    monthlyExtraRevenue = monthlyExtraTransactions * averageOrderValue;
    yearlyExtraRevenue = yearlyExtraTransactions * averageOrderValue;
  }
  
  return {
    dailyExtraTransactions,
    monthlyExtraTransactions,
    yearlyExtraTransactions,
    dailyExtraRevenue,
    monthlyExtraRevenue,
    yearlyExtraRevenue,
    lift: relativeLift * 100, // as percentage
    message: "Projected additional transactions if winning variant was deployed to all traffic"
  };
};

// Calculate probability of being winner for multiple variants
export const calculateWinnerProbabilities = (variants, iterations = 100000) => {
  const results = [];
  
  // Monte Carlo simulation
  const wins = new Array(variants.length).fill(0);
  
  for (let i = 0; i < iterations; i++) {
    const samples = variants.map(variant => {
      const alpha = 1 + variant.conversions;
      const beta = 1 + variant.sessions - variant.conversions;
      return betaRandomSample(alpha, beta);
    });
    
    // Find the variant with highest sample
    const maxSample = Math.max(...samples);
    const winnerIndex = samples.indexOf(maxSample);
    wins[winnerIndex]++;
  }
  
  // Calculate probabilities
  variants.forEach((variant, index) => {
    results.push({
      ...variant,
      winProbability: wins[index] / iterations,
      winPercentage: (wins[index] / iterations) * 100
    });
  });
  
  return results;
};

// Enhanced Bayesian analysis with more detailed results
export const enhancedBayesianABTest = (
  controlConversions, 
  controlSessions, 
  variantConversions, 
  variantSessions, 
  priorAlpha = 1, 
  priorBeta = 1,
  iterations = 100000
) => {
  // Basic bayesian test
  const basicResult = bayesianABTest(
    controlConversions,
    controlSessions,
    variantConversions,
    variantSessions,
    priorAlpha,
    priorBeta
  );
  
  // Calculate probability density data
  const densityData = generateProbabilityDensityData(
    controlConversions,
    controlSessions,
    variantConversions,
    variantSessions,
    priorAlpha,
    priorBeta
  );
  
  // Calculate winner probabilities - only for this specific variant vs control
  const variants = [
    { name: 'Control', conversions: controlConversions, sessions: controlSessions, isControl: true },
    { name: 'Variant', conversions: variantConversions, sessions: variantSessions, isControl: false }
  ];
  
  const winnerProbabilities = calculateWinnerProbabilities(variants, iterations);
  
  // Calculate expected loss
  const posteriorAlphaControl = priorAlpha + controlConversions;
  const posteriorBetaControl = priorBeta + controlSessions - controlConversions;
  const posteriorAlphaVariant = priorAlpha + variantConversions;
  const posteriorBetaVariant = priorBeta + variantSessions - variantConversions;
  
  // Expected values
  const controlExpected = posteriorAlphaControl / (posteriorAlphaControl + posteriorBetaControl);
  const variantExpected = posteriorAlphaVariant / (posteriorAlphaVariant + posteriorBetaVariant);
  
  return {
    ...basicResult,
    densityData,
    winnerProbabilities,
    expectedValues: {
      control: controlExpected,
      variant: variantExpected,
      lift: (variantExpected - controlExpected) / controlExpected
    },
    posteriorParameters: {
      control: { alpha: posteriorAlphaControl, beta: posteriorBetaControl },
      variant: { alpha: posteriorAlphaVariant, beta: posteriorBetaVariant }
    }
  };
};

// Calculate overall winner probabilities for all variants together (like CXL)
export const calculateOverallWinnerProbabilities = (allVariants, iterations = 100000) => {
  const results = [];
  
  // Monte Carlo simulation
  const wins = new Array(allVariants.length).fill(0);
  
  for (let i = 0; i < iterations; i++) {
    const samples = allVariants.map(variant => {
      const alpha = 1 + variant.conversions;
      const beta = 1 + variant.sessions - variant.conversions;
      return betaRandomSample(alpha, beta);
    });
    
    // Find the variant with highest sample
    const maxSample = Math.max(...samples);
    const winnerIndex = samples.indexOf(maxSample);
    wins[winnerIndex]++;
  }
  
  // Calculate probabilities - these will sum to 100%
  allVariants.forEach((variant, index) => {
    results.push({
      ...variant,
      winProbability: wins[index] / iterations,
      winPercentage: (wins[index] / iterations) * 100
    });
  });
  
  return results;
};

// Inverse normal distribution (quantile function)
export const normalInverse = (p, mean = 0, stdDev = 1) => {
  // Beasley-Springer-Moro algorithm
  const a = [0, -3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
  const b = [0, -5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
  const c = [0, -7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
  const d = [0, 7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];

  if (p < 0 || p > 1) {
    throw new Error("Probability must be between 0 and 1");
  }

  let q, r, x;

  if (p === 0) return Number.NEGATIVE_INFINITY;
  if (p === 1) return Number.POSITIVE_INFINITY;

  if (p < 0.02425) {
    q = Math.sqrt(-2 * Math.log(p));
    x = (((((c[1] * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) * q + c[6]) / ((((d[1] * q + d[2]) * q + d[3]) * q + d[4]) * q + 1);
  } else if (p < 0.97575) {
    q = p - 0.5;
    r = q * q;
    x = (((((a[1] * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * r + a[6]) * q / (((((b[1] * r + b[2]) * r + b[3]) * r + b[4]) * r + b[5]) * r + 1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    x = -(((((c[1] * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) * q + c[6]) / ((((d[1] * q + d[2]) * q + d[3]) * q + d[4]) * q + 1);
  }

  return mean + stdDev * x;
};

// Chi-squared distribution
export const chiSquaredCDF = (x, df) => {
  if (x < 0) return 0;
  return incompleteGamma(df / 2, x / 2) / gamma(df / 2);
};

// Incomplete gamma function
export const incompleteGamma = (a, x) => {
  if (x < 0 || a <= 0) return 0;
  
  if (x < a + 1) {
    // Series expansion
    let sum = 1;
    let term = 1;
    for (let n = 1; n < 100; n++) {
      term *= x / (a + n - 1);
      sum += term;
      if (Math.abs(term) < 1e-15) break;
    }
    return Math.exp(-x + a * Math.log(x) - Math.log(gamma(a))) * sum;
  } else {
    // Continued fraction
    let b = x + 1 - a;
    let c = 1e30;
    let d = 1 / b;
    let h = d;
    
    for (let i = 1; i < 100; i++) {
      const an = -i * (i - a);
      b += 2;
      d = an * d + b;
      if (Math.abs(d) < 1e-30) d = 1e-30;
      c = b + an / c;
      if (Math.abs(c) < 1e-30) c = 1e-30;
      d = 1 / d;
      const del = d * c;
      h *= del;
      if (Math.abs(del - 1) < 1e-15) break;
    }
    
    return gamma(a) - Math.exp(-x + a * Math.log(x) - Math.log(gamma(a))) * h;
  }
};

// Beta distribution functions
export const betaCDF = (x, alpha, beta) => {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  return incompleteBeta(x, alpha, beta);
};

export const incompleteBeta = (x, a, b) => {
  if (x < 0 || x > 1) return 0;
  
  const bt = Math.exp(
    Math.log(gamma(a + b)) - Math.log(gamma(a)) - Math.log(gamma(b)) + 
    a * Math.log(x) + b * Math.log(1 - x)
  );
  
  if (x < (a + 1) / (a + b + 2)) {
    return bt * betaContinuedFraction(x, a, b) / a;
  } else {
    return 1 - bt * betaContinuedFraction(1 - x, b, a) / b;
  }
};

export const betaContinuedFraction = (x, a, b) => {
  const maxIterations = 100;
  const epsilon = 1e-15;
  
  let am = 1;
  let bm = 1;
  let az = 1;
  let qab = a + b;
  let qap = a + 1;
  let qam = a - 1;
  let bz = 1 - qab * x / qap;
  
  for (let m = 1; m <= maxIterations; m++) {
    const em = m;
    const tem = em + em;
    let d = em * (b - m) * x / ((qam + tem) * (a + tem));
    
    let ap = az + d * am;
    let bp = bz + d * bm;
    d = -(a + em) * (qab + em) * x / ((a + tem) * (qap + tem));
    
    const app = ap + d * az;
    const bpp = bp + d * bz;
    const aold = az;
    
    am = ap / bpp;
    bm = bp / bpp;
    az = app / bpp;
    bz = 1;
    
    if (Math.abs(az - aold) < epsilon * Math.abs(az)) {
      return az;
    }
  }
  
  return az;
};

// Z-test for proportions
export const zTestProportions = (p1, n1, p2, n2, alpha = 0.05) => {
  const pPool = (p1 * n1 + p2 * n2) / (n1 + n2);
  const se = Math.sqrt(pPool * (1 - pPool) * (1/n1 + 1/n2));
  const z = (p1 - p2) / se;
  const pValueTwoTailed = 2 * (1 - normalCDF(Math.abs(z)));
  
  return {
    zStatistic: z,
    pValue: pValueTwoTailed,
    isSignificant: pValueTwoTailed < alpha,
    confidenceLevel: (1 - alpha) * 100
  };
};

// Chi-squared test for independence
export const chiSquaredTest = (observed, expected, alpha = 0.05) => {
  if (observed.length !== expected.length) {
    throw new Error("Observed and expected arrays must have the same length");
  }
  
  let chiSquared = 0;
  for (let i = 0; i < observed.length; i++) {
    if (expected[i] > 0) {
      chiSquared += Math.pow(observed[i] - expected[i], 2) / expected[i];
    }
  }
  
  const df = observed.length - 1;
  const pValue = 1 - chiSquaredCDF(chiSquared, df);
  
  return {
    chiSquared,
    degreesOfFreedom: df,
    pValue,
    isSignificant: pValue < alpha,
    confidenceLevel: (1 - alpha) * 100
  };
};

// Sample size calculation for AB testing
export const calculateSampleSize = (
  baselineRate, 
  minDetectableEffect, 
  alpha = 0.05, 
  power = 0.8, 
  variants = 2
) => {
  const zAlpha = Math.abs(normalInverse(alpha / 2));
  const zBeta = Math.abs(normalInverse(1 - power));
  
  const p1 = baselineRate;
  const p2 = baselineRate * (1 + minDetectableEffect);
  
  const pBar = (p1 + p2) / 2;
  const delta = Math.abs(p2 - p1);
  
  const n = Math.pow(zAlpha * Math.sqrt(2 * pBar * (1 - pBar)) + zBeta * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2)), 2) / Math.pow(delta, 2);
  
  return Math.ceil(n * variants);
};

// Minimum detectable effect calculation
export const calculateMDE = (
  sampleSize, 
  baselineRate, 
  alpha = 0.05, 
  power = 0.8, 
  variants = 2
) => {
  const zAlpha = Math.abs(normalInverse(alpha / 2));
  const zBeta = Math.abs(normalInverse(1 - power));
  
  const n = sampleSize / variants;
  const p = baselineRate;
  
  const mde = (zAlpha + zBeta) * Math.sqrt(p * (1 - p) * (2 / n));
  
  return {
    absoluteMDE: mde,
    relativeMDE: (mde / p) * 100,
    minConversionRate: p + mde
  };
};

// Test duration calculation
export const calculateTestDuration = (
  sampleSize, 
  dailyTraffic, 
  trafficSplit = 0.5
) => {
  const dailyTestTraffic = dailyTraffic * trafficSplit;
  const duration = Math.ceil(sampleSize / dailyTestTraffic);
  
  return {
    durationDays: duration,
    durationWeeks: Math.ceil(duration / 7),
    recommendedMinimum: Math.max(duration, 7) // Minimum 1 week
  };
};

// Bayesian A/B test analysis
export const bayesianABTest = (
  controlConversions, 
  controlSessions, 
  variantConversions, 
  variantSessions, 
  priorAlpha = 1, 
  priorBeta = 1
) => {
  // Posterior parameters
  const posteriorAlphaControl = priorAlpha + controlConversions;
  const posteriorBetaControl = priorBeta + controlSessions - controlConversions;
  
  const posteriorAlphaVariant = priorAlpha + variantConversions;
  const posteriorBetaVariant = priorBeta + variantSessions - variantConversions;
  
  // Monte Carlo simulation to calculate probability
  const iterations = 100000;
  let variantWins = 0;
  
  for (let i = 0; i < iterations; i++) {
    // Generate random samples from Beta distributions
    const controlSample = betaRandomSample(posteriorAlphaControl, posteriorBetaControl);
    const variantSample = betaRandomSample(posteriorAlphaVariant, posteriorBetaVariant);
    
    if (variantSample > controlSample) {
      variantWins++;
    }
  }
  
  const probabilityVariantWins = variantWins / iterations;
  
  return {
    probabilityVariantWins,
    probabilityControlWins: 1 - probabilityVariantWins,
    credibleInterval: calculateCredibleInterval(posteriorAlphaVariant, posteriorBetaVariant),
    isSignificant: probabilityVariantWins > 0.95 || probabilityVariantWins < 0.05
  };
};

// Beta random sample generator
export const betaRandomSample = (alpha, beta) => {
  const x = gammaRandomSample(alpha, 1);
  const y = gammaRandomSample(beta, 1);
  return x / (x + y);
};

// Gamma random sample generator (using Marsaglia and Tsang method)
export const gammaRandomSample = (shape, scale) => {
  if (shape < 1) {
    // Use Johnk's generator
    const c = 1 / shape;
    const d = 1 / (1 - shape);
    
    while (true) {
      const u = Math.random();
      const v = Math.random();
      const x = Math.pow(u, c);
      const y = Math.pow(v, d);
      
      if (x + y <= 1) {
        if (x + y > 0) {
          return scale * x * Math.pow(-Math.log(Math.random()), 1 / shape) / (x + y);
        }
      }
    }
  } else {
    // Use Marsaglia and Tsang method
    const d = shape - 1 / 3;
    const c = 1 / Math.sqrt(9 * d);
    
    while (true) {
      let x, v;
      do {
        x = normalRandomSample();
        v = 1 + c * x;
      } while (v <= 0);
      
      v = v * v * v;
      const u = Math.random();
      
      if (u < 1 - 0.0331 * x * x * x * x) {
        return scale * d * v;
      }
      
      if (Math.log(u) < 0.5 * x * x + d * (1 - v + Math.log(v))) {
        return scale * d * v;
      }
    }
  }
};

// Normal random sample generator (Box-Muller transform)
export const normalRandomSample = (mean = 0, stddev = 1) => {
  let u1, u2, z0;
  
  if (normalRandomSample.spare) {
    z0 = normalRandomSample.spare;
    normalRandomSample.spare = null;
  } else {
    u1 = Math.random();
    u2 = Math.random();
    
    const z1 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    z0 = Math.sqrt(-2 * Math.log(u1)) * Math.sin(2 * Math.PI * u2);
    
    normalRandomSample.spare = z0;
    z0 = z1;
  }
  
  return mean + stddev * z0;
};

// Calculate credible interval for Beta distribution
export const calculateCredibleInterval = (alpha, beta, confidence = 0.95) => {
  const lowerTail = (1 - confidence) / 2;
  const upperTail = 1 - lowerTail;
  
  // Approximation using normal distribution for large alpha + beta
  if (alpha + beta > 50) {
    const mean = alpha / (alpha + beta);
    const variance = (alpha * beta) / (Math.pow(alpha + beta, 2) * (alpha + beta + 1));
    const stddev = Math.sqrt(variance);
    
    const zScore = Math.abs(normalInverse(lowerTail));
    
    return {
      lower: Math.max(0, mean - zScore * stddev),
      upper: Math.min(1, mean + zScore * stddev)
    };
  }
  
  // For smaller samples, use beta quantile approximation
  return {
    lower: betaInverse(lowerTail, alpha, beta),
    upper: betaInverse(upperTail, alpha, beta)
  };
};

// Beta distribution inverse (quantile function) - approximation
export const betaInverse = (p, alpha, beta) => {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  
  // Initial guess using normal approximation
  const mean = alpha / (alpha + beta);
  const variance = (alpha * beta) / (Math.pow(alpha + beta, 2) * (alpha + beta + 1));
  let x = mean + normalInverse(p) * Math.sqrt(variance);
  x = Math.max(0.001, Math.min(0.999, x));
  
  // Newton-Raphson iteration
  for (let i = 0; i < 20; i++) {
    const cdf = betaCDF(x, alpha, beta);
    const pdf = Math.exp((alpha - 1) * Math.log(x) + (beta - 1) * Math.log(1 - x) - Math.log(gamma(alpha)) - Math.log(gamma(beta)) + Math.log(gamma(alpha + beta)));
    
    if (Math.abs(cdf - p) < 1e-10) break;
    
    const newX = x - (cdf - p) / pdf;
    x = Math.max(0.001, Math.min(0.999, newX));
  }
  
  return x;
};

// Utility functions for formatting results
export const formatPValue = (pValue) => {
  if (pValue < 0.001) return "< 0.001";
  if (pValue < 0.01) return pValue.toFixed(3);
  return pValue.toFixed(2);
};

export const formatPercentage = (value, decimals = 2) => {
  return (value * 100).toFixed(decimals) + "%";
};

export const formatNumber = (value, decimals = 0) => {
  return value.toLocaleString(undefined, { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Format currency
export const formatCurrency = (value, currency = 'USD', decimals = 2) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

// Confidence interval for conversion rate
export const conversionRateConfidenceInterval = (conversions, sessions, confidence = 0.95) => {
  const p = conversions / sessions;
  const z = Math.abs(normalInverse((1 - confidence) / 2));
  const margin = z * Math.sqrt(p * (1 - p) / sessions);
  
  return {
    lower: Math.max(0, p - margin),
    upper: Math.min(1, p + margin),
    margin
  };
};

// Statistical power calculation
export const calculateStatisticalPower = (
  sampleSize, 
  baselineRate, 
  effectSize, 
  alpha = 0.05
) => {
  const n = sampleSize / 2; // per group
  const p1 = baselineRate;
  const p2 = baselineRate * (1 + effectSize);
  
  const pPool = (p1 + p2) / 2;
  const se = Math.sqrt(pPool * (1 - pPool) * (2 / n));
  const delta = Math.abs(p2 - p1);
  
  const zAlpha = Math.abs(normalInverse(alpha / 2));
  const zBeta = (delta - zAlpha * se) / Math.sqrt(p1 * (1 - p1) / n + p2 * (1 - p2) / n);
  
  return normalCDF(zBeta);
}; 